import { fmt, pct, sanitizeExportSheetName } from "./overviewExportCommon";

export { csvEscape, toCSV } from "./overviewExportCommon";

function buildQuestionSummaryRows(question) {
  const rows = [
    ["Question", question.title ?? ""],
    ["Type", question.type ?? ""],
    ["Answered", question.answered ?? ""],
    ["Total players", question.total ?? ""],
  ];

  if (question.type === "scale") {
    rows.push(
      ["Average", fmt(question.currentAverage)],
      ["Previous average", fmt(question.previousAverage)],
      ["Min", fmt(question.min)],
      ["Max", fmt(question.max)]
    );
  }

  if (question.type === "yesno") {
    rows.push(
      ["Yes count", question.yesCount ?? ""],
      ["No count", question.noCount ?? ""],
      ["Yes rate", pct(question.yesRate)],
      ["Previous yes rate", pct(question.previousYesRate)]
    );
  }

  if (question.type === "choice") {
    rows.push(
      ["Response rate", pct(question.responseRate)],
      ["Previous response rate", pct(question.previousResponseRate)]
    );
  }

  if (question.type === "time") {
    rows.push(["Average hour", Number.isFinite(question.averageHour) ? `${fmt(question.averageHour, 0)}h` : ""]);
  }

  if (question.type === "text") {
    rows.push(["Responses", question.count ?? ""], ["Previous responses", question.previousCount ?? ""]);
  }

  if (question.type === "bodymap" && Array.isArray(question.bodymapTypeAverages)) {
    question.bodymapTypeAverages.forEach((entry) => {
      rows.push(
        [`${entry.label} average`, fmt(entry.current)],
        [`${entry.label} previous average`, fmt(entry.previous)]
      );
    });
  }

  return rows;
}

function buildDistributionRows(question) {
  const distribution = question.distribution || [];

  if (question.type === "choice") {
    return [
      ["Option", "Count", "Percent", "Previous percent", "Delta"],
      ...distribution.map((entry) => [
        entry.label ?? "",
        entry.count ?? "",
        pct(entry.percent),
        pct(entry.previousPercent),
        Number.isFinite(entry.delta) ? pct(entry.delta) : "",
      ]),
    ];
  }

  if (question.type === "scale") {
    return [
      ["Bucket", "Count"],
      ...distribution.map((entry) => [entry.label ?? "", entry.count ?? ""]),
    ];
  }

  if (question.type === "yesno") {
    return [
      ["Answer", "Count"],
      ...distribution.map((entry) => [entry.label ?? "", entry.count ?? ""]),
    ];
  }

  if (question.type === "time") {
    return [
      ["Period", "Count"],
      ...distribution.map((entry) => [entry.label ?? "", entry.count ?? ""]),
    ];
  }

  return [["Label", "Count"], ...distribution.map((entry) => [entry.label ?? "", entry.count ?? ""])];
}

function buildTrendRows(question) {
  const trend = question.trend || [];
  if (!trend.length) return null;

  if (question.type === "scale") {
    return [
      ["Date", "Average"],
      ...trend.map((entry) => [entry.label ?? "", fmt(entry.average)]),
    ];
  }

  if (question.type === "yesno") {
    return [
      ["Date", "Yes rate"],
      ...trend.map((entry) => [entry.label ?? "", pct(entry.yesRate)]),
    ];
  }

  if (question.type === "time") {
    return [
      ["Date", "Average hour"],
      ...trend.map((entry) => [
        entry.label ?? "",
        Number.isFinite(entry.averageHour) ? `${fmt(entry.averageHour, 0)}h` : "",
      ]),
    ];
  }

  return null;
}

function buildBodymapZonesRows(question) {
  const zones = question.topZones || [];
  if (!zones.length) return null;

  return [
    ["Zone", "Count"],
    ...zones.map((entry) => [entry.label ?? "", entry.count ?? ""]),
  ];
}

export function buildSubjectiveOverviewSheets(data = {}) {
  const { summary = {}, questions = [], playerAnswers = [], playersWide = [] } = data;

  const rowsSummary = [
    ["Field", "Value"],
    ["Date", data.selectedDate ?? ""],
    ["Template", data.templateName ?? ""],
    ["Session", data.sessionLabel ?? ""],
    ["Players", summary.playerCount ?? ""],
    ["Answered players", summary.answeredPlayers ?? ""],
    ["Completion rate", pct(summary.completionRate)],
    ["Answered questions", summary.answeredQuestions ?? ""],
    ["Total questions", summary.totalQuestions ?? ""],
  ];

  const rowsQuestions = [
    ["Question", "Type", "Answered", "Total", "Metric", "Value"],
  ];

  (questions || []).forEach((question) => {
    buildQuestionSummaryRows(question)
      .slice(4)
      .forEach(([metric, value]) => {
        rowsQuestions.push([
          question.title ?? "",
          question.type ?? "",
          question.answered ?? "",
          question.total ?? "",
          metric,
          value ?? "",
        ]);
      });

    if (buildQuestionSummaryRows(question).length <= 4) {
      rowsQuestions.push([
        question.title ?? "",
        question.type ?? "",
        question.answered ?? "",
        question.total ?? "",
        "",
        "",
      ]);
    }
  });

  const rowsAnswers = [
    ["Player", "Question", "Type", "Answer", "Detail", "Submitted at"],
    ...playerAnswers.map((row) => [
      row.playerName ?? "",
      row.questionTitle ?? "",
      row.questionType ?? "",
      row.answer ?? "",
      row.detail ?? "",
      row.submittedAt ?? "",
    ]),
  ];

  const wideHeaders = playersWide.length ? Object.keys(playersWide[0]) : ["Player"];
  const rowsPlayersWide = [
    wideHeaders,
    ...playersWide.map((row) => wideHeaders.map((header) => row[header] ?? "")),
  ];

  const sheets = {
    Summary: rowsSummary,
    Questions: rowsQuestions,
    Answers: rowsAnswers,
    "Players.Wide": rowsPlayersWide,
  };

  (questions || []).forEach((question, index) => {
    const baseName = sanitizeExportSheetName(question.type, index);

    if (question.distribution?.length) {
      sheets[`${baseName}.Dist`] = buildDistributionRows(question);
    }

    const trendRows = buildTrendRows(question);
    if (trendRows) {
      sheets[`${baseName}.Trend`] = trendRows;
    }

    const zoneRows = buildBodymapZonesRows(question);
    if (zoneRows) {
      sheets[`${baseName}.Zones`] = zoneRows;
    }
  });

  return sheets;
}

export function buildSubjectiveOverviewSingleCsvRows(data = {}) {
  const sheets = buildSubjectiveOverviewSheets(data);
  const rows = [];
  const push = (...r) => rows.push(r);

  push(["dataset", "section", "field", "value"]);
  push(["meta", "summary", "date", data?.selectedDate ?? ""]);
  push(["meta", "summary", "template", data?.templateName ?? ""]);
  push(["meta", "summary", "session", data?.sessionLabel ?? ""]);

  const summary = data?.summary || {};
  [
    ["players", summary.playerCount],
    ["answered_players", summary.answeredPlayers],
    ["completion_rate", summary.completionRate],
    ["answered_questions", summary.answeredQuestions],
    ["total_questions", summary.totalQuestions],
  ].forEach(([field, value]) => {
    push(["meta", "summary", field, Number.isFinite(value) ? (field.includes("rate") ? pct(value) : value) : value ?? ""]);
  });

  push([]);
  push(["Questions", "question", "type", "answered", "total", "metric", "value"]);
  (sheets.Questions || []).slice(1).forEach((row) => {
    push(["row", ...row]);
  });

  push([]);
  push(["Answers", "player", "question", "type", "answer", "detail", "submitted_at"]);
  (data?.playerAnswers || []).forEach((row) => {
    push([
      "row",
      row.playerName ?? "",
      row.questionTitle ?? "",
      row.questionType ?? "",
      row.answer ?? "",
      row.detail ?? "",
      row.submittedAt ?? "",
    ]);
  });

  push([]);
  const wideHeaders = (data?.playersWide || []).length ? Object.keys(data.playersWide[0]) : [];
  if (wideHeaders.length) {
    push(["PlayersWide", ...wideHeaders]);
    (data?.playersWide || []).forEach((row) => {
      push(["row", ...wideHeaders.map((header) => row[header] ?? "")]);
    });
  }

  (data?.questions || []).forEach((question, index) => {
    const baseName = sanitizeExportSheetName(question.type, index);

    const distSheet = sheets[`${baseName}.Dist`];
    if (distSheet?.length) {
      push([]);
      push([`${baseName}.Dist`, ...(distSheet[0] || [])]);
      distSheet.slice(1).forEach((row) => push(["row", ...row]));
    }

    const trendSheet = sheets[`${baseName}.Trend`];
    if (trendSheet?.length) {
      push([]);
      push([`${baseName}.Trend`, ...(trendSheet[0] || [])]);
      trendSheet.slice(1).forEach((row) => push(["row", ...row]));
    }

    const zonesSheet = sheets[`${baseName}.Zones`];
    if (zonesSheet?.length) {
      push([]);
      push([`${baseName}.Zones`, ...(zonesSheet[0] || [])]);
      zonesSheet.slice(1).forEach((row) => push(["row", ...row]));
    }
  });

  return rows;
}

export function buildSubjectiveOverviewFileBase(selectedDate) {
  const safeDate = String(selectedDate || "day").replace(/[^\d-]/g, "") || "day";
  return `subjective_overview_${safeDate}`;
}
